/* eslint-disable react-hooks/exhaustive-deps */
import {
  HighlightOffOutlined,
  DeleteOutline,
  AddCircleOutline,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Grid,
  TextField,
  Tab,
  Tabs,
  Box,
  Button,
  Typography,
  Paper,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Fragment, useState, useEffect, useRef } from 'react';
import { useUpsertPresetMutation } from 'src/backend';
import LoadingButton from 'src/components/LoadingButton';
import MediaSelectionDialog from 'src/pages/MediaManagement/MediaSelectionDialog';
import MediaSelector from 'src/pages/MediaManagement/MediaSelector';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { MachineType } from '../Dashboard/CreateEditMachineGroupForm';

export type BarcodeModel = {
  code: string;
  description: string;
};

export type SettingSection = {
  length: number;
  fill: number;
  quantity: number;
};

export type PresetMediaItem = {
  id: string;
  fileName: string;
  folderId?: string;
  fullFileUrl: string;
  thumbnailUrl: string;
  relatedPresets: string[];
};

export type StepSettings = {
  sections: SettingSection[];
  length?: number;
  fill?: number;
};

export type EasyPackStepModel = {
  sortOrder: number;
  text: string;
  media?: PresetMediaItem;
  delay?: number;
  delayType: string;
  operation: string;
  length?: number;
};

export type SmartBaggerStepModel = {
  sortOrder: number;
  text: string;
  media?: PresetMediaItem;
  delay?: number;
  delayType: string;
  bagType: string;
  settings: StepSettings;
};

export type PresetFullModel = {
  id?: string | undefined;
  name: string;
  description: string;
  barCodes: BarcodeModel[];
  batch: number;
  smartBaggerSteps?: SmartBaggerStepModel[];
  easyPackSteps?: EasyPackStepModel[];
  length: number;
  width: number;
  depth: number;
  boxType: string;
  boxDescription: string;
  partNumber: string;
};

enum StepType {
  Bag = 'Bag',
  None = 'None',
}

enum DelayType {
  Delay = 'Delay',
  ManuallySequenced = 'Manually-Sequenced',
}

enum OperationType {
  None = 'None',
  Pad = 'Pad',
}

type Params = {
  machineGroupId: string;
  customerId: string;
  machineType: string;
  presetId?: string | null;
  preset?: PresetFullModel | null;
  closeForm: (refetchRequired: boolean) => void;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`preset-tabpanel-${index}`}
      aria-labelledby={`preset-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: '3 3 1 3' }}>{children}</Box>}
    </div>
  );
}

function UpsertPresetForm({
  machineGroupId,
  customerId,
  machineType,
  presetId,
  preset,
  closeForm,
}: Params) {
  const [tabValue, setTabValue] = useState(0);
  const [upsertPreset, { isLoading }] = useUpsertPresetMutation();
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);

  const defaultValues: PresetFullModel = {
    name: '',
    description: '',
    barCodes: [],
    batch: 1,
    smartBaggerSteps: [],
    easyPackSteps: [],
    length: 0,
    width: 0,
    depth: 0,
    boxType: '',
    boxDescription: '',
    partNumber: '',
    ...preset,
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PresetFullModel>({
    defaultValues,
  });

  const {
    fields: barCodeFields,
    append: appendBarcode,
    remove: removeBarcode,
  } = useFieldArray({
    control,
    name: 'barCodes',
  });

  const {
    fields: smartBaggerFields,
    append: appendSmartBaggerStep,
    remove: removeSmartBaggerStep,
  } = useFieldArray({
    control,
    name: 'smartBaggerSteps',
  });

  const {
    fields: easyPackFields,
    append: appendEasyPackStep,
    remove: removeEasyPackStep,
  } = useFieldArray({
    control,
    name: 'easyPackSteps',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const onSubmit = (data: PresetFullModel) => {
    upsertPreset({
      groupId: machineGroupId,
      upsertPresetRequest: {
        preset: data,
        presetId,
        machineType,
      },
    })
      .unwrap()
      .then(() => closeForm(true))
      .catch();
  };

  const appendNewSmartBaggerStep = () => {
    appendSmartBaggerStep({
      sortOrder: smartBaggerFields.length,
      text: '',
      delayType: DelayType.ManuallySequenced,
      bagType: StepType.None,
      settings: { sections: [] },
    });
  };

  // Media selection handlers
  const handleSelectMediaForStep = (stepIndex: number) => {
    setCurrentStepIndex(stepIndex);
    setMediaDialogOpen(true);
  };

  const handleMediaSelection = (media: PresetMediaItem) => {
    if (currentStepIndex !== null) {
      if (machineType === MachineType[MachineType.SmartBagger]) {
        setValue(`smartBaggerSteps.${currentStepIndex}.media`, media);
      } else if (machineType === MachineType[MachineType.EasyPack]) {
        setValue(`easyPackSteps.${currentStepIndex}.media`, media);
      }
    }
    setMediaDialogOpen(false);
    setCurrentStepIndex(null);
  };

  const handleRemoveMediaFromStep = (stepIndex: number) => {
    if (machineType === MachineType[MachineType.SmartBagger]) {
      setValue(`smartBaggerSteps.${stepIndex}.media`, undefined);
    } else if (machineType === MachineType[MachineType.EasyPack]) {
      setValue(`easyPackSteps.${stepIndex}.media`, undefined);
    }
  };

  const handleCloseMediaDialog = () => {
    setMediaDialogOpen(false);
    setCurrentStepIndex(null);
  };

  const initialStepAddedRef = useRef(false);

  useEffect(() => {
    if (!presetId && !initialStepAddedRef.current) {
      const length = watch('length');
      const width = watch('width');
      const depth = watch('depth');
      const boxType = watch('boxType');
      const boxDescription = watch('boxDescription');

      const formattedText = `L:${
        typeof length === 'number' ? length.toFixed(3) : '0'
      }, W:${typeof width === 'number' ? width.toFixed(1) : '0'}, D:${
        typeof depth === 'number' ? depth.toFixed(1) : '0'
      } Box:${boxType || ''}${boxDescription ? ` – ${boxDescription}` : ''}`;

      if (machineType === MachineType[MachineType.SmartBagger]) {
        if (smartBaggerFields.length === 0) {
          appendSmartBaggerStep({
            sortOrder: 0,
            text: formattedText,
            delayType: DelayType.ManuallySequenced,
            bagType: StepType.None,
            settings: { sections: [] },
          });
          initialStepAddedRef.current = true;
        }
      } else if (machineType === MachineType[MachineType.EasyPack]) {
        if (easyPackFields.length === 0) {
          appendEasyPackStep({
            sortOrder: 0,
            text: formattedText,
            delayType: DelayType.ManuallySequenced,
            operation: OperationType.None,
          });
          initialStepAddedRef.current = true;
        }
      }
    }
  }, [
    presetId,
    machineType,
    watch,
    smartBaggerFields.length,
    appendSmartBaggerStep,
    easyPackFields.length,
    appendEasyPackStep,
  ]);

  // Separate effect to update the step text when box dimensions change
  useEffect(() => {
    if (!presetId && initialStepAddedRef.current) {
      const length = watch('length');
      const width = watch('width');
      const depth = watch('depth');
      const boxType = watch('boxType');
      const boxDescription = watch('boxDescription');

      const formattedText = `L:${
        typeof length === 'number' ? length.toFixed(3) : '0'
      }, W:${typeof width === 'number' ? width.toFixed(3) : '0'}, D:${
        typeof depth === 'number' ? depth.toFixed(3) : '0'
      } Box:${boxType || ''}${boxDescription ? ` – ${boxDescription}` : ''}`;

      if (machineType === MachineType[MachineType.SmartBagger]) {
        if (smartBaggerFields.length > 0) {
          setValue(`smartBaggerSteps.0.text`, formattedText);
        }
      } else if (machineType === MachineType[MachineType.EasyPack]) {
        if (easyPackFields.length > 0) {
          setValue(`easyPackSteps.0.text`, formattedText);
        }
      }
    }
  }, [
    watch('length'),
    watch('width'),
    watch('depth'),
    watch('boxType'),
    watch('boxDescription'),
    presetId,
    machineType,
    smartBaggerFields.length,
    easyPackFields.length,
    initialStepAddedRef.current,
    setValue,
  ]);

  useEffect(() => {
    const name = watch('name');
    if (name) {
      setValue('partNumber', name);
    }
  }, [watch('name'), setValue]);

  return (
    <Dialog
      open
      onClose={() => closeForm(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '2rem', padding: '20px' }}>
        {presetId ? 'Edit Preset' : 'Create New Preset'}
        <IconButton
          onClick={() => closeForm(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <HighlightOffOutlined fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="preset form tabs"
          >
            <Tab label="Basic Information" />
            <Tab label="Box Sizes" />
            <Tab label="Steps" />
          </Tabs>
        </Box>

        <form id="preset-form" onSubmit={handleSubmit(onSubmit)}>
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
              Preset Info
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required', maxLength: 40 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Preset Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Preset Description"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>

              {barCodeFields.map((field, index) => (
                <Fragment key={field.id}>
                  <Grid item xs={5}>
                    <Controller
                      name={`barCodes.${index}.code`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Barcode" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Controller
                      name={`barCodes.${index}.description`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Description" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <IconButton
                      onClick={() => removeBarcode(index)}
                      color="error"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Grid>
                </Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => appendBarcode({ code: '', description: '' })}
                  sx={{ mb: 2, width: '20%' }}
                  startIcon={<AddCircleOutline />}
                >
                  Add Barcode
                </Button>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
              Box Sizes
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Controller
                  name="boxType"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Box Type" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="boxDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Box Description" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="length"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value && (value <= 0 || value > 999.999))
                        return 'Must be between 0 and 999.999';
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Length"
                      type="text"
                      fullWidth
                      error={!!errors.length}
                      helperText={errors.length?.message}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ''
                        ) {
                          field.onChange(
                            e.target.value === '' ? 0 : e.target.value,
                          );
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value)) {
                          field.onChange(value);
                        }
                      }}
                      value={
                        typeof field.value === 'number'
                          ? field.value.toFixed(3)
                          : field.value
                      }
                      inputProps={{ step: 0.001 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="width"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value && (value <= 0 || value > 999.999))
                        return 'Must be between 0 and 999.999';
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Width"
                      type="text"
                      fullWidth
                      error={!!errors.width}
                      helperText={errors.width?.message}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ''
                        ) {
                          field.onChange(
                            e.target.value === '' ? 0 : e.target.value,
                          );
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value)) {
                          field.onChange(value);
                        }
                      }}
                      value={
                        typeof field.value === 'number'
                          ? field.value.toFixed(3)
                          : field.value
                      }
                      inputProps={{ step: 0.001 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="depth"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value && (value <= 0 || value > 999.999))
                        return 'Must be between 0 and 999.999';
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Depth"
                      type="text"
                      fullWidth
                      error={!!errors.depth}
                      helperText={errors.depth?.message}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ''
                        ) {
                          field.onChange(
                            e.target.value === '' ? 0 : e.target.value,
                          );
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!Number.isNaN(value)) {
                          field.onChange(value);
                        }
                      }}
                      value={
                        typeof field.value === 'number'
                          ? field.value.toFixed(3)
                          : field.value
                      }
                      inputProps={{ step: 0.001 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h2" gutterBottom sx={{ mb: 2 }}>
              Steps Configuration
            </Typography>
            {machineType === MachineType[MachineType.SmartBagger] ? (
              <Box sx={{ mb: 2 }}>
                {smartBaggerFields.map((field, index) => (
                  <Paper key={field.id} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <Typography variant="subtitle2">
                          Step {index}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          onClick={() => removeSmartBaggerStep(index)}
                          color="error"
                        >
                          Remove
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name={`smartBaggerSteps.${index}.text`}
                          control={control}
                          rules={{
                            required: 'Text is required',
                            validate: (value) => {
                              if (value.length > 75)
                                return 'Text cannot exceed 75 characters';
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Text"
                              error={!!errors.smartBaggerSteps?.[index]?.text}
                              helperText={
                                errors.smartBaggerSteps?.[index]?.text?.message
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`smartBaggerSteps.${index}.bagType`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>Bag Type</InputLabel>
                              <Select {...field} label="Bag Type">
                                <MenuItem value={StepType.Bag}>Bag</MenuItem>
                                <MenuItem value={StepType.None}>None</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`smartBaggerSteps.${index}.delayType`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>Delay Type</InputLabel>
                              <Select {...field} label="Delay Type">
                                <MenuItem value={DelayType.Delay}>
                                  Delay
                                </MenuItem>
                                <MenuItem value={DelayType.ManuallySequenced}>
                                  Manually Sequenced
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                      {watch(`smartBaggerSteps.${index}.delayType`) ===
                        DelayType.Delay && (
                        <Grid item xs={6}>
                          <Controller
                            name={`smartBaggerSteps.${index}.delay`}
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (value === undefined) return 'Required';
                                if (value && (value <= 0 || value > 30.9))
                                  return 'Must be between 0 and 30.9';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Delay (seconds)"
                                type="number"
                                fullWidth
                                error={
                                  !!errors.smartBaggerSteps?.[index]?.delay
                                }
                                helperText={
                                  errors.smartBaggerSteps?.[index]?.delay
                                    ?.message
                                }
                                inputProps={{ step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                      {watch(`smartBaggerSteps.${index}.bagType`) ===
                        StepType.Bag && (
                        <>
                          <Grid item xs={6}>
                            <Controller
                              name={`smartBaggerSteps.${index}.settings.length`}
                              control={control}
                              rules={{
                                validate: (value) => {
                                  if (value === undefined) return 'Required';
                                  if (value && (value < 10 || value > 70))
                                    return 'Must be between 10 and 70';
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Length (inches)"
                                  type="number"
                                  fullWidth
                                  error={
                                    !!errors.smartBaggerSteps?.[index]?.settings
                                      ?.length
                                  }
                                  helperText={
                                    errors.smartBaggerSteps?.[index]?.settings
                                      ?.length?.message
                                  }
                                  inputProps={{ step: 1 }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Controller
                              name={`smartBaggerSteps.${index}.settings.fill`}
                              control={control}
                              rules={{
                                validate: (value) => {
                                  if (value && (value < 20 || value > 100))
                                    return 'Must be between 20 and 100';
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Fill (%)"
                                  type="number"
                                  fullWidth
                                  error={
                                    !!errors.smartBaggerSteps?.[index]?.settings
                                      ?.fill
                                  }
                                  helperText={
                                    errors.smartBaggerSteps?.[index]?.settings
                                      ?.fill?.message
                                  }
                                  inputProps={{ step: 1 }}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <MediaSelector
                          customerId={customerId}
                          selectedMedia={watch(
                            `smartBaggerSteps.${index}.media`,
                          )}
                          onSelectMedia={() => handleSelectMediaForStep(index)}
                          onRemoveMedia={() => handleRemoveMediaFromStep(index)}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  onClick={appendNewSmartBaggerStep}
                  startIcon={<AddCircleOutline />}
                >
                  Add Step
                </Button>
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                {easyPackFields.map((field, index) => (
                  <Paper key={field.id} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={10}>
                        <Typography variant="subtitle2">
                          Step {index}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          onClick={() => removeEasyPackStep(index)}
                          color="error"
                        >
                          Remove
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          name={`easyPackSteps.${index}.text`}
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} label="Step Text" fullWidth />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`easyPackSteps.${index}.operation`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>Operation</InputLabel>
                              <Select {...field} label="Operation">
                                <MenuItem value={OperationType.None}>
                                  None
                                </MenuItem>
                                <MenuItem value={OperationType.Pad}>
                                  Pad
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`easyPackSteps.${index}.delayType`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>Delay Type</InputLabel>
                              <Select {...field} label="Delay Type">
                                <MenuItem value={DelayType.Delay}>
                                  Delay
                                </MenuItem>
                                <MenuItem value={DelayType.ManuallySequenced}>
                                  Manually Sequenced
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>
                      {watch(`easyPackSteps.${index}.delayType`) ===
                        DelayType.Delay && (
                        <Grid item xs={6}>
                          <Controller
                            name={`easyPackSteps.${index}.delay`}
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (value === undefined) return 'Required';
                                if (value && (value <= 0 || value > 30.0))
                                  return 'Must be between 0 and 30.0';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Delay (seconds)"
                                type="number"
                                fullWidth
                                error={!!errors.easyPackSteps?.[index]?.delay}
                                helperText={
                                  errors.easyPackSteps?.[index]?.delay?.message
                                }
                                inputProps={{ step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                      {watch(`easyPackSteps.${index}.operation`) ===
                        OperationType.Pad && (
                        <Grid item xs={6}>
                          <Controller
                            name={`easyPackSteps.${index}.length`}
                            control={control}
                            rules={{
                              validate: (value) => {
                                if (value === undefined) return 'Required';
                                if (value && (value < 6 || value > 999))
                                  return 'Must be between 6 and 999';
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Pad Length (inches)"
                                type="number"
                                fullWidth
                                error={!!errors.easyPackSteps?.[index]?.length}
                                helperText={
                                  errors.easyPackSteps?.[index]?.length?.message
                                }
                                onChange={(e) => {
                                  if (
                                    /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                                    e.target.value === ''
                                  ) {
                                    field.onChange(
                                      e.target.value === ''
                                        ? 0
                                        : e.target.value,
                                    );
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (!Number.isNaN(value)) {
                                    field.onChange(value);
                                  }
                                }}
                                value={
                                  typeof field.value === 'number'
                                    ? field.value.toFixed(1)
                                    : field.value
                                }
                                inputProps={{ step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <MediaSelector
                          customerId={customerId}
                          selectedMedia={watch(`easyPackSteps.${index}.media`)}
                          onSelectMedia={() => handleSelectMediaForStep(index)}
                          onRemoveMedia={() => handleRemoveMediaFromStep(index)}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  onClick={() =>
                    appendEasyPackStep({
                      sortOrder: easyPackFields.length,
                      text: '',
                      delayType: DelayType.ManuallySequenced,
                      operation: OperationType.None,
                    })
                  }
                  startIcon={<AddCircleOutline />}
                >
                  Add Step
                </Button>
              </Box>
            )}
          </TabPanel>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => closeForm(false)}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          type="submit"
          form="preset-form"
        >
          {presetId ? 'Update Preset' : 'Create Preset'}
        </LoadingButton>
      </DialogActions>

      <MediaSelectionDialog
        isOpen={mediaDialogOpen}
        specificCustomerId={customerId}
        onClose={handleCloseMediaDialog}
        onSelectMedia={handleMediaSelection}
        selectedMediaId={(() => {
          if (currentStepIndex !== null) {
            if (machineType === MachineType[MachineType.SmartBagger]) {
              return watch(`smartBaggerSteps.${currentStepIndex}.media`)?.id;
            }
            if (machineType === MachineType[MachineType.EasyPack]) {
              return watch(`easyPackSteps.${currentStepIndex}.media`)?.id;
            }
          }
          return undefined;
        })()}
      />
    </Dialog>
  );
}

export default UpsertPresetForm;
